import React, { useEffect, useState, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useAuth } from '../../../context/AuthContext';
import Api, { getWebSocketToken } from '../../../services/api';
// componentes:
import {
    Row,
    Typography,
    Collapse,
    Select,
    Radio,
    notification,
    Input,
    Table,
    Modal,
    Space,
    Upload,
    Button,
    InputNumber,
    DatePicker,
    Flex,
    Tooltip,
    Form,
} from 'antd';
import { UploadOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import EditableTable from '../../../components/EditableTable';
import { capitalize } from '../../../utils/string';
import * as mappings from '../../../utils/mappings';
 
dayjs.extend(dayjsBusinessDays);

const { RangePicker } = DatePicker;

const getInitialValue = (question, answer) => {
    if (!answer) return undefined;

    switch (question.field_type.slug) {
        case 'select':
            return answer.option?.length ? answer.option[0].id : undefined;
        case 'boolean':
            return Number(answer.value);
        case 'date':
            if (question.is_range) {
                return answer.value ? toDateRange(answer.value) : undefined;
            }
            return answer.value ? dayjs(answer.value, 'DD/MM/YYYY') : undefined;
        default:
            // Para input, text, number, etc.
            return answer.value;
    }
};

/**
 * Transforma o intervalo de datas especificado
 * em um objeto dayjs.
 * 
 * @param {String} value
 * @returns {Array}
 */
const toDateRange = value => {
    const [startDate, endDate] = value.split('~');

    return [
        dayjs(startDate, 'DD/MM/YYYY'),
        dayjs(endDate, 'DD/MM/YYYY')
    ];
};

/**
 * 
 * @param {String} value
 * @returns {React.JSX.Element}
 */
const renderDateRangeDiff = value => {
    if (!value) {
        return;
    }

    const [startDate, endDate] = toDateRange(value);
    const diff = endDate.add(1, 'day').diff(startDate, 'days');
    const businessDiff = endDate.add(1, 'day').businessDiff(startDate, 'days');

    return (
        <Typography.Text
            style={{
                display: 'block',
                marginTop: 5,
                textAlign: 'right'
            }}
        >
            <b>{diff}</b> dias corridos | <b>{businessDiff}</b> dias úteis
        </Typography.Text>
    );
};

function ReportTemplateForm({ report, setReport, readOnly, form }) {
    const {
        user: {
            id: userId,
            full_name: userFullName
        },
        authUserBelongsToGroups
    } = useAuth();
    const [isDirty, setIsDirty] = useState(false);
    const [largeTextModal, setLargeTextModal] = useState(null);
    const [activeColab, setActiveColab] = useState({});
    const activeColabRef = useRef({});
    const focusedQuestionRef = useRef(null);
    const socketRef = useRef(null);

    const [api, contextHolder] = notification.useNotification({
        placement: 'top',
        maxCount: 1
    });

    useEffect(() => {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const wsBaseUrl = `${wsProtocol}://${process.env.REACT_APP_API_HOST}:${process.env.REACT_APP_API_PORT}`;
        let disposed = false;
        let pageIsLeaving = false;
        let reconnectTimer;
        let heartbeatTimer;

        const sendFocusedQuestion = (socket, focusedQuestion) => {
            if (socket?.readyState === WebSocket.OPEN && focusedQuestion) {
                socket.send(JSON.stringify(focusedQuestion));
            }
        };

        const connect = async () => {
            if (disposed || pageIsLeaving) return;

            let wsToken;

            try {
                wsToken = await getWebSocketToken();
            } catch {
                if (!disposed && !pageIsLeaving) {
                    window.location.href = `${process.env.REACT_APP_EGIDE_URL}/logout`;
                }

                return;
            }

            if (disposed || pageIsLeaving) return;

            const socket = new WebSocket(
                `${wsBaseUrl}/ws/report/${report.id}/?token=${encodeURIComponent(wsToken)}`
            );
            socketRef.current = socket;

            socket.onmessage = event => {
                const data = JSON.parse(event.data);

                if (data?.current_state) {
                    const nextColab = Object.fromEntries(
                        Object.entries(data.current_state).filter(([, colab]) => (
                            colab?.type === 'focus' && colab.userId !== userId
                        ))
                    );

                    activeColabRef.current = nextColab;
                    setActiveColab(nextColab);

                    const focusedQuestion = focusedQuestionRef.current;
                    if (focusedQuestion && !nextColab[focusedQuestion.questionId]) {
                        sendFocusedQuestion(socket, focusedQuestion);
                    }
                    return;
                }

                if (data.userId === userId) return;

                const nextColab = { ...activeColabRef.current };
                if (data.type === 'focus') {
                    nextColab[data.questionId] = data;
                } else if (nextColab[data.questionId]?.userId === data.userId) {
                    delete nextColab[data.questionId];
                }

                activeColabRef.current = nextColab;
                setActiveColab(nextColab);
            };

            socket.onclose = event => {
                if (socketRef.current === socket) {
                    socketRef.current = null;
                    activeColabRef.current = {};
                    setActiveColab({});
                }

                if (!disposed && !pageIsLeaving) {
                    reconnectTimer = window.setTimeout(connect, 2000);
                }
            };

            socket.onerror = () => socket.close();
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;

            const focusedQuestion = focusedQuestionRef.current;
            const activeQuestionColab = focusedQuestion && activeColabRef.current[focusedQuestion.questionId];
            if (!activeQuestionColab || activeQuestionColab.userId === userId) {
                sendFocusedQuestion(socketRef.current, focusedQuestion);
            }
        };

        const closeSocket = () => {
            const socket = socketRef.current;
            const focusedQuestion = focusedQuestionRef.current;

            if (socket?.readyState === WebSocket.OPEN) {
                if (focusedQuestion) {
                    socket.send(JSON.stringify({
                        ...focusedQuestion,
                        type: 'blur'
                    }));
                }
            }

            if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
                socket.close();
            }
        };

        connect();
        heartbeatTimer = window.setInterval(() => {
            if (focusedQuestionRef.current && socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, 5000);

        const onPageHide = () => {
            pageIsLeaving = true;
            window.clearTimeout(reconnectTimer);
            closeSocket();
            socketRef.current = null;
            activeColabRef.current = {};
            setActiveColab({});
        };

        const onPageShow = event => {
            if (event.persisted) {
                pageIsLeaving = false;
                if (!socketRef.current) connect();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('pageshow', onPageShow);

        return () => {
            disposed = true;
            window.clearTimeout(reconnectTimer);
            window.clearInterval(heartbeatTimer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('pageshow', onPageShow);
            closeSocket();
            socketRef.current = null;
            focusedQuestionRef.current = null;
        };
    }, [report.id, userId, userFullName]);

    useEffect(() => {
        const onBeforeUnload = event => {
            // requisita a confirmação do usuário
            // se existirem dados não salvos:
            if (isDirty) {
                event.preventDefault();
            }
        };

        window.addEventListener('beforeunload', onBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [isDirty]);

    const submitFormData = async dispatcher => {
        if (readOnly) {
            return;
        }

        try {
            await dispatcher();

            const lastHistory = report.history.sort((a, b) => b.id - a.id)[0];

            if (lastHistory.id) {
                setReport(previousState => ({
                    ...previousState,
                    history: [
                        {
                            user: {
                                full_name: 'você'
                            },
                            status: {
                                name: 'Editado',
                                slug: mappings.status.EDITADO
                            },
                        },
                        ...previousState.history
                    ]
                }));
            }

            api.success({ message: 'Suas alterações foram salvas automaticamente.' });
        } catch {
            api.error({ message: 'Não foi possível salvar as alterações.' });
        }
    }

    const onSelectChange = (question, value) => {
        form.setFieldValue(String(question.id), value);
        const payload = {
            answers: [
                {
                    question_id: question.id,
                    option_id: value
                }
            ]
        };

        submitFormData(() => Api(`/reports/${report.id}/answers`).store(payload));
    }

    const sendEvent = (eventType, questionId, event) => {
        const currentColab = activeColabRef.current[questionId];
        const hasActiveColab = currentColab?.type === 'focus' && currentColab.userId !== userId;

        if (eventType === 'focus' && hasActiveColab) {
            event.preventDefault();
            return;
        }

        const payload = {
            type: eventType,
            reportId: report.id,
            questionId,
            userId,
            userFullName
        };

        if (eventType === 'focus') {
            focusedQuestionRef.current = payload;
        } else if (focusedQuestionRef.current?.questionId === questionId) {
            focusedQuestionRef.current = null;
        }

        const socket = socketRef.current;
        if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
        }
    }

    const handleChange = (question, { target }) => {
        form.setFieldValue(String(question.id), target.value);
        const payload = {
            answers: [
                {
                    question_id: question.id,
                    value: target.value
                }
            ]
        };

        submitFormData(() => Api(`/reports/${report.id}/answers`).store(payload));
    }

    const debounceHandleChange = useDebouncedCallback(handleChange, 1000);

    /**
     * Mapeia a opção para um item de seleção.
     * 
     * @param {Object} option 
     * @returns {Object}
     */
    const toSelectOption = option => ({
        value: option.id,
        label: option.key
    });

    const columnMappings = {
        id: '#',
        data_inicial: 'Data Inicial',
        data_final: 'Data Final',
        defensoria_id: 'Nº Defensoria',
        defensoria: 'Defensoria',
        defensor_id: 'Nº Defensor',
        defensor: 'Defensor',
        atuacao: 'Atuação',
        documento: 'Documento',
        titulo: 'Título',
        recebidos: 'Recebidos',
        redistribuidos: 'Redistribuídos',
        descricao: 'Descrição',
    };

    // colunas que serão suprimidas da visualização:
    const hiddenColumns = ['id', 'defensoria_id', 'defensor_id'];

    /**
     * Deriva os nomes das colunas da tabela a partir
     * das chaves dos registros.
     * 
     * @param {Array} columns 
     * @returns {Array}
     */
    const toTableColumns = columns => columns.filter(
        column => !hiddenColumns.includes(column)
    )
        .map(column => ({
            key: column,
            dataIndex: column,
            title: columnMappings[column] ?? column,
            render: value => {
                const maxLength = 70;
                const title = columnMappings[column] ?? column;

                if (typeof value === 'string') {
                    if (columns.length > 2 && (value?.length > maxLength)) {
                        return (
                            <>
                                {title.toLowerCase() !== 'anexos' && (
                                    <>{value.slice(0, maxLength)}...</>
                                )}
                                
                                <Button
                                    onClick={() => {
                                        setLargeTextModal({
                                            title,
                                            content: value
                                        })
                                    }}
                                    type="link"
                                >
                                    ler mais
                                </Button>
                            </>
                        );
                    }
                }

                return value;
            }
        }));

    /**
     * Manipula a submissão dos registros
     * da tabela para a API.
     * 
     * @param {number} questionId
     */
    const handleTableSave = questionId => async ({ dataSource: incomingDataSource }) => {
        try {
            const payload = {
                answers: [
                    {
                        question_id: questionId,
                        data: incomingDataSource.map(row => {
                            delete row.key
    
                            return row
                        })
                    }
                ]
            }
    
            await submitFormData(() => Api(`/reports/${report.id}/answers`).store(payload));

            return true;
        } catch {
            return false;
        }
    };

    /**
     * Renderiza o campo de entrada da questão e,
     * se houver, a resposta associada.
     * 
     * @param {Object} question os atributos da questão
     * @param {Object} answer os atributos da resposta associada
     * @returns {React.JSX.Element}
     */
    const renderQuestionInputByFieldType = (question, answer) => {
        // esta questão está sendo editado por outros usuários?:
        const hasActiveColab = activeColab?.[question.id] && (
            activeColab[question.id].type === 'focus' &&
            activeColab[question.id].questionId === question.id
        );

        const styleAttrs = {
            border: hasActiveColab ? '3px solid #1677ff' : null,
        }

        const componentByFieldTypeSlug = {
            input: (
                <Input
                    key={question.id}
                    style={{ width: '100%', maxWidth: 800, ...styleAttrs }}
                    placeholder={question.placeholder ?? 'Este campo ainda não foi preenchido'}
                    required={question.is_required}
                    //defaultValue={answer?.value}
                    allowClear
                    onFocus={event => sendEvent('focus', question.id, event)}
                    onBlur={() => sendEvent('blur', question.id)}
                    onChange={event => debounceHandleChange(question, event)}
                    disabled={readOnly || question.readOnly || hasActiveColab}
                />
            ),
            text: (
                <Input.TextArea
                    key={question.id}
                    style={{ width: '100%', maxWidth: 800, ...styleAttrs }}
                    rows={6}
                    placeholder={question.placeholder ?? 'Este campo ainda não foi preenchido'}
                    required={question.is_required}
                    //defaultValue={answer?.value}
                    allowClear
                    onFocus={event => sendEvent('focus', question.id, event)}
                    onBlur={() => sendEvent('blur', question.id)}
                    onChange={event => debounceHandleChange(question, event)}
                    disabled={readOnly || question.readOnly || hasActiveColab}
                />
            ),
            number: (
                <InputNumber
                    key={question.id}
                    style={{ width: '100%', maxWidth: 800, ...styleAttrs }}
                    placeholder={question.placeholder ?? 'Este campo ainda não foi preenchido'}
                    //defaultValue={answer?.value}
                    onFocus={event => sendEvent('focus', question.id, event)}
                    onBlur={() => sendEvent('blur', question.id)}
                    onChange={value => handleChange(question, { target: { value } })}
                    disabled={readOnly || question.readOnly || hasActiveColab}
                    min={0}
                />
            ),
            date: question.is_range ? (
                <div key={question.id} style={{ maxWidth: 800 }}>
                    <RangePicker
                        style={{ width: '100%', ...styleAttrs }}
                        onChange={(_, [startDate, endDate]) => {
                            debounceHandleChange(question, {
                                target: {
                                    value: startDate ? `${startDate}~${endDate}` : null
                                }
                            })
                        }}
                        onFocus={event => sendEvent('focus', question.id, event)}
                        onBlur={() => sendEvent('blur', question.id)}
                        defaultValue={answer?.value ? toDateRange(answer.value) : null}
                        format={{
                            format: 'DD/MM/YYYY',
                            type: 'mask'
                        }}
                        disabled={readOnly || question.readOnly || hasActiveColab}
                    />

                    {renderDateRangeDiff(answer?.value)}
                </div>
            ) : (
                <DatePicker
                    key={question.id}
                    style={{ width: '100%', maxWidth: 800, ...styleAttrs }}
                    //defaultValue={answer?.value ? dayjs(answer.value, 'DD/MM/YYYY') : null}
                    onChange={(_, targetDate) => debounceHandleChange(question, { target: { value: targetDate } })}
                    onFocus={event => sendEvent('focus', question.id, event)}
                    onBlur={() => sendEvent('blur', question.id)}
                    format={{
                        format: 'DD/MM/YYYY',
                        type: 'mask'
                    }}
                    allowClear
                    disabled={readOnly || question.readOnly || hasActiveColab}
                />
            ),
            select: (
                <Select
                    key={question.id}
                    allowClear
                    style={{ width: '100%', maxWidth: 800, ...styleAttrs }}
                    placeholder={question.placeholder ?? 'Este campo ainda não foi preenchido'}
                    //defaultValue={answer?.option?.length ? answer.option[0]?.id : null}
                    options={question.options.map(toSelectOption)}
                    onChange={value => onSelectChange(question, value)}
                    onFocus={event => sendEvent('focus', question.id, event)}
                    onBlur={() => sendEvent('blur', question.id)}
                    disabled={readOnly || question.readOnly || hasActiveColab}
                />
            ),
            boolean: (
                <Radio.Group
                    key={question.id}
                    onChange={event => handleChange(question, event)}
                    //defaultValue={Number(answer?.value)}
                    disabled={readOnly || question.readOnly}
                >
                    <Radio value={1}>Sim</Radio>
                    <Radio value={0}>Não</Radio>
                </Radio.Group>
            ),
            table: () => {
                const tableConfig = {
                    showHeader: true,
                    // ...
                };

                if (answer?.data) {
                    // determina se deve exibir o cabeçalho da tabela:
                    tableConfig.showHeader = (
                        answer.data.columns.length > 0
                        // se não for o padrão antigo:
                        && !answer.data.columns.includes('column1')
                    );

                    // determina se esta é uma tabela somente com dados nativos:
                    const isNativeData = answer.metadata.sources.filter(source => source !== 'native').length === 0;

                    // configurações para registros sincronizados de
                    // sistemas externos:
                    if (answer.data.rows.length && !isNativeData) {
                        const sources = answer.metadata.sources.map(capitalize).join('/');
                        const resources = answer.metadata.resources.map(resourceName => capitalize(resourceName.replaceAll("_", " ")));

                        tableConfig.footer = () => (
                            <div style={{ textAlign: 'center' }}>
                                <Typography.Text type="secondary">
                                    <small>Registros de <b>{resources}</b> (Fonte: <b>{sources}</b>)</small>
                                </Typography.Text>
                            </div>
                        )
                    }
                }

                const hasValues = row => {
                    return !Object.values(row).every(value => value === null);
                };

                return (
                    <Space key={question.id} direction="vertical" style={{ width: '100%', textAlign: 'right' }}>
                        {answer?.data && (
                            <>
                                {(readOnly || question.is_read_only) ? (
                                    <Table
                                        {...tableConfig}
                                        size="small"
                                        dataSource={answer.data.rows.filter(hasValues)}
                                        columns={toTableColumns(answer.data.columns)}
                                        pagination={false}
                                        bordered
                                        scroll={{ x: 'max-content' }}
                                    />
                                ) : (
                                    <EditableTable
                                        readOnly={readOnly}
                                        showHeader={tableConfig.showHeader}
                                        initialDataSource={answer.data.rows}
                                        initialColumns={toTableColumns(answer.data.columns)}
                                        onSave={handleTableSave(question.id)}
                                        onDirty={setIsDirty}
                                    />
                                )}
                            </>
                        )}
                    </Space>
                );
            },
            file: (
                <Upload
                    key={question.id}
                    multiple
                    beforeUpload={() => false}
                    onChange={async ({ file }) => {
                        try {
                            // se a mudança for para remover o arquivo:
                            if (file.status === 'removed') {
                                await Api(`/reports/${report.id}/answers/${answer.id}/attachments/?attachment_id=${file.uid}`).delete();
                            } else {
                                const formData = new FormData();
        
                                formData.append('question_id', question.id);
                                formData.append('file', file);
    
                                await Api(`/reports/${report.id}/answers`).store(formData, {
                                    headers: {
                                        'Content-Type': 'multipart/form-data'
                                    }
                                });
                            }
    
                            api.success({ message: 'Suas alterações foram salvas automaticamente.' });
                        } catch {
                            api.error({ message: 'Não foi possível salvar as alterações.' });            
                        }
                    }}
                    defaultFileList={
                        answer?.attachments.map(file => ({
                            uid: file.id,
                            name: file.short_filename,
                            url: `${process.env.REACT_APP_API_URL}/api/v1/renew-attachment/AnswerAttachment/${file.uuid}`
                        }))
                    }
                    showUploadList={{
                        showRemoveIcon: !readOnly && !question.readOnly
                    }}
                >
                    <Space direction="vertical">
                        {(!readOnly && !question.readOnly) && (
                            <Button icon={<UploadOutlined />}>Enviar Documento</Button>
                        )}

                        {question.description && (
                            <Typography.Text type="secondary">
                                <small>{question.description}</small>
                            </Typography.Text>
                        )}
                    </Space>
                </Upload>
            ),
            // ...
        };

        const component = componentByFieldTypeSlug[question.field_type.slug];

        if (typeof component === 'function') {
            return component();
        }

        return (
            <>
                {component}

                {hasActiveColab && (
                    <>
                        <br />

                        <Typography.Text style={{ color: '#1677ff' }}>
                            <b>Em edição por {activeColab[question.id].userFullName}</b>
                        </Typography.Text>
                    </>
                )}
            </>
        );
    };

    /**
     * Renderiza as questões da seção.
     * 
     * @param {number[]} incomingIndexes 
     * @param {Object} incomingSectionConfig 
     * @returns {(question: Object, questionIndex: Number) => React.JSX.Element}
     */
    const renderQuestion = (incomingIndexes, incomingSectionConfig, sectionContent = []) => (question, questionIndex) => {
        const [answer] = report.answers.filter(answer => answer.question.id === question.id);

        if (question.groups.length) {
            const questionGroups = question.groups.map(group => group.group.name);

            if (!authUserBelongsToGroups(questionGroups)) {
                return null;
            }
        }

        // TRAVA DE RELATÓRIO: Verifica se é o template alvo (usando includes para evitar problemas com espaços em branco)
        const isRmaEstagio = report.template?.name?.includes('RMA Estágio Probatório');

        // TRAVA DE SEÇÃO: O primeiro dígito do índice de aninhamento deve ser 5
        const isSection5 = incomingIndexes[0] === 5;

        // APLICAÇÃO DA REGRA: Só será tratado como dinâmico se passar em todas as travas
        const isObservacao = question.title?.trim() === 'Observação:' && isRmaEstagio && isSection5;

        // Lógica dinâmica para a pergunta "Observação"
        if (isObservacao) {
            // Encontra todas as outras perguntas na mesma seção/subseção
            const siblingQuestions = sectionContent.filter(item => 'field_type' in item && item.id !== question.id);
            const siblingIds = siblingQuestions.map(item => String(item.id));

            return (
                <Form.Item
                    key={`wrapper-${question.id}`}
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => {
                        // Re-renderiza o campo Observação se o valor de qualquer pergunta vizinha for alterado
                        return siblingIds.some(id => prevValues[id] !== currentValues[id]);
                    }}
                >
                    {({ getFieldValue }) => {
                        let isDynamicRequired = false;

                        for (const siblingQ of siblingQuestions) {
                            // Tenta ler o valor recém alterado pelo usuário direto da instância do Form
                            const selectedOptionId = getFieldValue(String(siblingQ.id));
                            
                            if (selectedOptionId) {
                                const selectedOption = siblingQ.options?.find(opt => String(opt.id) === String(selectedOptionId));
                                if (selectedOption && String(selectedOption.value) !== "1") { // 1 = Ótimo
                                    isDynamicRequired = true;
                                    break;
                                }
                            } else {
                                // Fallback de renderização inicial: lê do state report.answers (JSON original)
                                const [siblingAnswer] = report.answers.filter(ans => ans.question.id === siblingQ.id);
                                if (siblingAnswer?.option?.length) {
                                    if (String(siblingAnswer.option[0].value) !== "1") {
                                        isDynamicRequired = true;
                                        break;
                                    }
                                }
                            }
                        }

                        return (
                            <Form.Item
                                key={question.id}
                                name={String(question.id)}
                                dependencies={siblingIds}
                                initialValue={getInitialValue(question, answer)}
                                label={
                                    <span>
                                        <b>{incomingIndexes.join('.')}.{questionIndex + 1}.</b> {question.title}
                                    </span>
                                }
                                rules={isDynamicRequired ? [{ required: true, message: 'Este campo é obrigatório pois a avaliação foi diferente de "Ótimo".' }] : []}
                            >
                                {renderQuestionInputByFieldType({ ...incomingSectionConfig, ...question }, answer)}
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            );
        }

        return (
            <Form.Item
                key={question.id}
                name={String(question.id)}
                initialValue={getInitialValue(question, answer)}
                label={
                    <span>
                        <b>{incomingIndexes.join('.')}.{questionIndex + 1}.</b> {question.title}
                    </span>
                }
                rules={question.is_required ? [{ required: true, message: 'Este campo é obrigatório.' }] : []}
            >
                {renderQuestionInputByFieldType({ ...incomingSectionConfig, ...question }, answer)}
            </Form.Item>
        );
    }

    /**
     * Quando existente, utiliza o valor definido como posição ordinal.
     * Caso contrário, define a posição ordinal como sendo o maior
     * valor número possível, para que sua posição seja a última
     * na seção ou subseção.
     * 
     * @param {number|null} value - a posição ordinal da questão ou (sub)seção
     * @returns {number}
     */
    const safeOrdinal = value => value ?? Number.MAX_SAFE_INTEGER;

    /**
     * Recupera o conteúdo que será renderizado na seção, ordenados
     * pela sua posição ordinal.
     * 
     * @param {Object} section - o conteúdo da seção
     * @returns {React.JSX.Element[]}
     */
    const getOrderedSectionContent = ({ questions, subsections }) => {
        const byOrdinalPosition = (a, b) => safeOrdinal(a.ordinal_position) < safeOrdinal(b.ordinal_position);

        return [...questions, ...subsections].sort(byOrdinalPosition);
    }

    /**
     * Somente questões que não tenham sido desabilitadas
     * ou, as seções.
     * 
     * @param {Object} target - o elemento do conteúdo
     * @returns {Boolean}
     */
    const byActiveQuestion = target => {
        const isQuestion = "field_type" in target;

        if (!isQuestion || !target.disabled_at) {
            return true;
        }

        // que tenham sido desabilitados depois:
        const disabledAfterReportCreation = dayjs(target.disabled_at) >= dayjs(report.created_at);

        return disabledAfterReportCreation;
    };

    /**
     * Renderiza as seções e subseções do report.template.
     * 
     * @param {Object} config
     * @param {number[]} nestingIndexes
     * @returns {(section: Object, index: Number) => React.JSX.Element}
     */
    const renderSectionContent = (config, nestingIndexes) => (section, currentSubsectionIndex=1) => {
        const nextNestingIndexes = !config.sub ? nestingIndexes : [
            ...nestingIndexes, 
            // calcula o novo índice da subseção, quando aplicável:
            currentSubsectionIndex + 1
        ];

        /**
         * Renderiza o conteúdo da seção.
         * 
         * @param {Object} content
         * @param {number} index
         * @returns {React.JSX.Element}
        */
       const renderContent = (content, index) => {
            const isQuestion = 'field_type' in content;

            if (isQuestion) {
                return renderQuestion(nextNestingIndexes, section, sectionContent)(content, index)
            }

            return renderSectionContent({ sub: true }, nextNestingIndexes)(content, index);
        }

        // o conteúdo da seção, respeitando a posição ordinal
        // entre questões e subseções:
        const sectionContent = getOrderedSectionContent(section);
        const sectionSubmissions = report.submissions?.filter(submission => submission.section === section.id);

        return (
            <div key={nextNestingIndexes.join(".")}>
                {config.sub && (
                    <>
                        <Typography.Title level={5} style={{ marginBottom: section.description ? 0 : null }}>
                            <b>{nextNestingIndexes.join(".")}.</b> {section.name?.split("|")[0]}
                        </Typography.Title>
                    
                        {section.name && (
                            <div style={{ maxWidth: 900, marginBottom: 15 }}>
                                <Typography.Text type="secondary">
                                    {section.name.split("|")[1]}
                                </Typography.Text>
                            </div>
                        )}

                        {section.description && (
                            <div style={{ maxWidth: 900, marginTop:10, marginBottom: 15 }}>
                                <Typography.Text type="secondary">
                                    {section.description}
                                </Typography.Text>
                            </div>
                        )}
                    </>
                )}

                {/* Renderiza o conteúdo da seção ou subseção: */}
                {sectionContent.filter(byActiveQuestion).map(renderContent)}

                {/* As manifestações RESPONDIDAS, desta seção: */}
                {sectionSubmissions.map(submission => submission.answered_at && (
                    <Space key={submission.id} direction="vertical" style={{ width: '100%', maxWidth: 800 }}>
                        <Typography.Text>
                            Manifestação enviada por <b>{report.defender.user.full_name}</b> no dia <b>{dayjs(submission.answered_at).format('DD/MM/YYYY HH:mm')}</b>:
                        </Typography.Text>

                        <Input.TextArea
                            style={{ width: '100%' }}
                            value={submission.content}
                            autoSize
                            disabled
                        />

                        <Flex justify="end">
                            <Tooltip title="Copiar conteúdo para a área de transferência">
                                <Button
                                    type="dashed"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                        if (window.isSecureContext) {
                                            navigator.clipboard.writeText(submission.content)
                                                .then(() => {
                                                    api.success({ message: 'O conteúdo da manifestação foi copiado para a área de transferência.' })
                                                });
                                        } else {
                                            api.error({ message: 'Não é possível copiar o conteúdo da manifestação para a área de transferência, neste ambiente.' })
                                        }
                                    }}
                                >
                                    Copiar manifestação
                                </Button>
                            </Tooltip>
                        </Flex>
                    </Space>
                ))}
            </div>
        )
    };

    /**
     * Mapeia o objeto esperado pelo componente.
     * 
     * @param {Object} section 
     * @param {number} index
     * @returns {Object}
     */
    const toCollapseItem = (section, index) => {
        const sectionIndex = index + 1;

        return {
            key: section.id,
            label: (
                <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography.Text>
                        <b>{sectionIndex}. {section.name.toUpperCase()}</b><br/>

                        <Typography.Text type="secondary">
                            <small>{section.description}</small>
                        </Typography.Text>
                    </Typography.Text>
                </Row>
            ),
            children: renderSectionContent({ sub: false }, [sectionIndex])(section)
        };
    }

    return (
        <>
            {contextHolder}

            <Form form={form} layout="vertical">
                <Collapse
                    defaultActiveKey={report.template.sections.map(section => section.id)}
                    items={report.template.sections.map(toCollapseItem)}
                />
            </Form>

            <Modal
                title={largeTextModal?.title}
                open={!!largeTextModal}
                footer={null}
                onCancel={() => setLargeTextModal(null)}
            >
                <div dangerouslySetInnerHTML={{ __html: largeTextModal?.content }}></div>
            </Modal>
        </>
    );
}

export default ReportTemplateForm;
